import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CircleX } from 'lucide-react';
import { DataTableFacetedFilter } from './data-table-faceted-filter';
import { Table } from '@tanstack/react-table';
import { Box, BOX_BRANDS } from '@/lib/api/boxes';

/** Build filter options from the distinct values present in a column. */
function versionOptions(table: Table<Box>, columnId: string) {
  const values = table.getColumn(columnId)?.getFacetedUniqueValues();
  if (!values) return [];
  return Array.from(values.keys())
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .sort()
    .map((v) => ({ value: v, label: v }));
}

/** App-version options, displayed by bubble label (e.g. NV-LATEST) when the
 *  version maps to a labeled APK; the filter value stays the raw version. */
function appVersionOptions(table: Table<Box>) {
  const meta = table.options.meta as {
    labelsForVersion?: (version?: string) => string[];
  };
  return versionOptions(table, 'appVersion')
    .map(({ value }) => ({
      value,
      label: meta?.labelsForVersion?.(value)[0] ?? value,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function TableToolbar({ table }: { table: Table<Box> }) {
  return (
    <div className="flex items-center pb-4 gap-2">
      <Input
        name="filter"
        placeholder="Filter box name..."
        value={(table.getState().globalFilter as string) ?? ''}
        onChange={(event) => {
          table.setGlobalFilter(event.target.value);
        }}
        className="max-w-sm"
      />

      {table.getColumn('status') && (
        <DataTableFacetedFilter
          column={table.getColumn('status')}
          title="Status"
          options={[
            {
              value: 'UNKNOWN',
              label: 'Unknown',
            },
            {
              value: 'COMMAND_FAIL',
              label: 'Command fail',
            },
            {
              value: 'PING_FAIL',
              label: 'Ping fail',
            },
            {
              value: 'ONLINE',
              label: 'Online',
            },
          ]}
        />
      )}

      {table.getColumn('brand') && (
        <DataTableFacetedFilter
          column={table.getColumn('brand')}
          title="Brand"
          options={BOX_BRANDS.map((b) => ({ value: b, label: b }))}
        />
      )}

      {table.getColumn('group') && (
        <DataTableFacetedFilter
          column={table.getColumn('group')}
          title="Group"
          options={versionOptions(table, 'group')}
        />
      )}

      {table.getColumn('androidVersion') && (
        <DataTableFacetedFilter
          column={table.getColumn('androidVersion')}
          title="Android"
          options={versionOptions(table, 'androidVersion')}
        />
      )}

      {table.getColumn('appVersion') && (
        <DataTableFacetedFilter
          column={table.getColumn('appVersion')}
          title="App"
          options={appVersionOptions(table)}
        />
      )}

      {(table.getState().columnFilters.length > 0 ||
        !!table.getState().globalFilter) && (
        <Button
          variant="ghost"
          onClick={() => {
            table.resetColumnFilters();
            table.setGlobalFilter('');
          }}
          className="h-8 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive lg:px-3"
        >
          Reset
          <CircleX className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
